import { FC, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import dayjs from 'dayjs';
import { ChevronDown } from 'lucide-react';

import { Accordion, AccordionDetails, AccordionSummary, Typography } from '@mui/material';

import { useGetAllTicketsQuery } from '@services';

import { AppDispatch, getTicketsListSelector, setTickets } from '@store';

import { REWARD_TYPE_TO_TICKET_TYPE_MAP } from './constants';
import { AvailableTicketsProps } from './types';

import styles from './AvailableTickets.module.css';

import { TicketType } from 'v2/constants';

const { availableGoldenTickets } = styles;

export const AvailableTickets: FC<AvailableTicketsProps> = ({ rewardsDataValue = [], showConstantly }) => {
  const dispatch = useDispatch<AppDispatch>();

  const tickets = useSelector(getTicketsListSelector);

  const { data: allTickets, isSuccess: isTicketsLoaded } = useGetAllTicketsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (isTicketsLoaded) {
      dispatch(setTickets(allTickets));
    }
  }, [allTickets, isTicketsLoaded]);

  const rewardTypes = useMemo(() => {
    return rewardsDataValue.map((reward) => reward.type).filter((type, index, array) => array.indexOf(type) === index);
  }, [rewardsDataValue]);

  const groupedTickets = useMemo(() => {
    if (!tickets || tickets.length === 0) return {};

    return rewardTypes.reduce(
      (groups, rewardType) => {
        const ticketType = REWARD_TYPE_TO_TICKET_TYPE_MAP[rewardType as keyof typeof REWARD_TYPE_TO_TICKET_TYPE_MAP];

        const filteredTickets = tickets.filter((ticket) => {
          return ticket.type === ticketType;
        });

        if (filteredTickets.length > 0) {
          groups[rewardType] = filteredTickets;
        }

        return groups;
      },
      {} as Record<string, typeof tickets>
    );
  }, [tickets, rewardTypes]);

  if (rewardsDataValue.length === 0 && !showConstantly) {
    return null;
  }

  const hasTicketRewards = rewardTypes.length > 0;

  if (!showConstantly && !hasTicketRewards) {
    return null;
  }

  return (
    <>
      {Object.entries(groupedTickets).map(([rewardType, ticketsList]) => (
        <Accordion key={rewardType} classes={{ root: availableGoldenTickets }}>
          <AccordionSummary
            expandIcon={<ChevronDown />}
            aria-controls={`panel-${rewardType}-content`}
            id={`panel-${rewardType}-header`}
          >
            <Typography>Available Tickets</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ul style={{ margin: 0, listStyle: 'disc' }}>
              {ticketsList.map(({ id, date, title, description, type }, index) => (
                <li key={`${rewardType}-${index}`}>
                  {id}: {dayjs(date).format('YYYY-MM-DD')} {type === TicketType.CONTEST ? title : description}
                </li>
              ))}
            </ul>
          </AccordionDetails>
        </Accordion>
      ))}

      {showConstantly && Object.keys(groupedTickets).length === 0 && (
        <Accordion classes={{ root: availableGoldenTickets }}>
          <AccordionSummary
            expandIcon={<ChevronDown />}
            aria-controls="panel-all-tickets-content"
            id="panel-all-tickets-header"
          >
            <Typography variant="h6">All Available Tickets</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ul style={{ margin: 0, listStyle: 'disc' }}>
              {tickets.map(({ id, date, description }, index) => (
                <li key={index}>
                  {id}: {dayjs(date).format('YYYY-MM-DD')} {description}
                </li>
              ))}
            </ul>
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
};
